<template>
    <div class="request-parent-link" v-if="view">
        <span class="request-parent-link__text" v-if="view.anonymous">{{ view.message }}</span>

        <span class="request-parent-link__text" v-else>
            Связано с обращением
            <router-link class="request-parent-link__number" :to="parentLink">№{{ view.parentId }}</router-link>
        </span>

        <button
            class="request-parent-link__unlink"
            type="button"
            v-if="canUnlink"
            :disabled="isPending"
            @click="confirmUnlink"
        >
            Отвязать
        </button>
    </div>
</template>
<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import ModalNames from '@/js/enums/ModalNames';
import { getRelatedRequests, unlinkRequestFromParent } from '@/js/api';
import { errorMessage } from '@/js/error';
import { isStaff, parentLinkView } from '@/js/requestRelations';

/**
 * Связь обращения с массовой проблемой — строка под карточкой.
 *
 * Пользователь видит только анонимный факт: номер главного обращения ему не приходит
 * вовсе (бэкенд отдаёт лишь hasParentRequest), и запрос за связями для него не делается.
 * Исполнитель видит номер со ссылкой, если может открыть главное обращение; иначе —
 * то же анонимное сообщение. Администратор видит все связи.
 */
@Component
class RequestViewParentLink extends Vue {
    @Prop({ type: Object, required: true })
    request;

    related = null;
    isPending = false;

    fetchToken = 0;

    get roles() {
        return { isExecutor: this.$_isExecutor, isAdmin: this.$_isAdmin };
    }

    get staff() {
        return isStaff(this.roles);
    }

    get view() {
        return parentLinkView(this.request, this.related, this.roles);
    }

    get canUnlink() {
        return this.staff && !!this.view;
    }

    get parentLink() {
        return { name: 'request', params: { id: this.view.parentId, method: this.$route.params.method } };
    }

    async fetch() {
        const token = ++this.fetchToken;

        this.related = null;

        if (!this.staff || !this.request.hasParentRequest) {
            return;
        }

        try {
            const response = await getRelatedRequests(this.request.id);

            if (token === this.fetchToken) {
                this.related = response.data;
            }
        } catch (requestError) {
            // Без ответа остаётся анонимная строка — номер просто не показывается.
            if (token === this.fetchToken) {
                this.related = null;
            }
        }
    }

    confirmUnlink() {
        this.$showModal(ModalNames.CONFIRM, {
            title: 'Отвязать обращение',
            text: 'Отвязать это обращение от массовой проблемы? Само обращение не изменится.',
            cancelTitle: 'Отмена',
            confirmTitle: 'Отвязать',
            confirmAction: () => this.unlink(),
        });
    }

    async unlink() {
        this.isPending = true;

        try {
            const response = await unlinkRequestFromParent(this.request.id);

            this.$emit('changed', response.data);
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.isPending = false;
        }
    }

    @Watch('request.id')
    @Watch('request.hasParentRequest')
    onRequestChanged() {
        this.fetch();
    }

    mounted() {
        this.fetch();
    }
}

export default RequestViewParentLink;
</script>
<style lang="scss">
.request-parent-link {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    color: #484848;
    font-size: 0.875rem;
    background: #fff8e6;
    border: 1px solid #f3dfa8;
    border-radius: 12px;

    &__number {
        color: #1a73e8;
        font-weight: 600;

        &:hover {
            text-decoration: underline;
        }
    }

    &__unlink {
        color: #5f6368;
        font-size: 0.8125rem;

        &:hover:not(:disabled) {
            color: var(--error-color);
        }

        &:disabled {
            opacity: 0.4;
        }
    }
}
</style>
